import { useEffect, useId, useRef, useState } from 'react'
import { IconSearch } from '../icons.js'
import { SCREEN_TITLES, якорьНомера, type ScreenName } from '../router.js'
import { вКавычках } from './quotes.js'
import { подходитЗапросу } from './search.js'

/**
 * Узкий срез клиента: палитре нужны только названия того, во что можно
 * перейти (барьер 2 — клиент API и есть граница процесса для экранов
 * админки, см. Instances.tsx).
 */
export interface CommandPaletteClient {
  pipelines(): Promise<{ items: { id: string; name: string }[] }>
  instances(): Promise<{ items: { id: string; name: string }[] }>
}

export interface CommandPaletteProps {
  client: CommandPaletteClient
  onClose: () => void
  /** Переход по адресу портала. Отдельным проп — чтобы не звать window из тела компонента. */
  navigate: (path: string) => void
}

/**
 * Разделы, в которые можно попасть без выбранного объекта. Переписки и
 * контактов здесь нет по той же причине, по которой их нет в боковой
 * навигации: они принадлежат номеру, и открывать их «вообще» не во что.
 */
const РАЗДЕЛЫ: readonly ScreenName[] = [
  'instances',
  'pipelines',
  'credentials',
  'variables',
  'webhooks',
  'access',
]

type ВидЦели = 'раздел' | 'сценарий' | 'номер'

interface Цель {
  key: string
  title: string
  kind: ВидЦели
  path: string
}

const ПОДПИСЬ_ВИДА: Record<ВидЦели, string> = {
  раздел: 'Раздел',
  сценарий: 'Сценарий',
  номер: 'Номер',
}

/**
 * Держит выбранную строку в видимой части списка. `nearest` — чтобы
 * прокрутка шла на одну строку, а не бросала список в середину: выдача
 * должна ехать за курсором, а не прыгать под ним.
 *
 * Проверки на наличие метода не случайны: happy-dom, в котором идут тесты
 * экранов, `scrollIntoView` не реализует.
 */
function подтянутьВКадр(строка: HTMLLIElement | null): void {
  строка?.scrollIntoView?.({ block: 'nearest' })
}


/**
 * Командная палитра: перейти куда угодно, не снимая рук с клавиатуры.
 *
 * Отрисовывается только пока открыта — как и окно подтверждения: скрытая
 * стилями, она осталась бы в порядке обхода клавиатурой.
 */
export function CommandPalette(props: CommandPaletteProps) {
  const { client, onClose, navigate } = props
  const listId = useId()
  const optionId = useId()
  const поле = useRef<HTMLInputElement>(null)

  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const [pipelines, setPipelines] = useState<{ id: string; name: string }[]>([])
  const [instances, setInstances] = useState<{ id: string; name: string }[]>([])

  /*
   * Списки берутся один раз — на открытии палитры, а не на каждом нажатии
   * клавиши и не при загрузке портала.
   *
   * Не на нажатие: между «сце» и «сцен» ничего не меняется на сервере, а
   * поиск по десяткам названий на клиенте бесплатен — запрос на букву
   * отправил бы за сеанс поиска пять одинаковых ответов и заставил бы
   * список моргать.
   *
   * Не при загрузке портала: палитру открывают не всегда, а сценарий,
   * созданный минуту назад, обязан в ней находиться. Открытие — редкое
   * событие (несколько раз за сеанс), и один запрос на него дёшев.
   *
   * Отказ не показывается: палитра — способ попасть куда-то быстрее, а не
   * источник правды о данных. Если списки не пришли, она остаётся тем, чем
   * была всегда, — переходом по разделам.
   */
  useEffect(() => {
    let cancelled = false
    void client
      .pipelines()
      .then((page) => {
        if (!cancelled) setPipelines(page.items)
      })
      .catch(() => undefined)
    void client
      .instances()
      .then((page) => {
        if (!cancelled) setInstances(page.items)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [client])

  useEffect(() => {
    поле.current?.focus()
    const вернуться = document.activeElement
    return () => {
      if (вернуться instanceof HTMLElement) вернуться.focus()
    }
  }, [])

  const цели: Цель[] = [
    ...РАЗДЕЛЫ.map((screen) => ({
      key: `раздел-${screen}`,
      title: SCREEN_TITLES[screen],
      kind: 'раздел' as const,
      path: `/app/${screen}`,
    })),
    ...pipelines.map((pipeline) => ({
      key: `сценарий-${pipeline.id}`,
      title: pipeline.name,
      kind: 'сценарий' as const,
      path: `/app/pipelines/${pipeline.id}`,
    })),
    ...instances.map((instance) => ({
      key: `номер-${instance.id}`,
      title: instance.name,
      kind: 'номер' as const,
      // Своего экрана у номера нет: карточка живёт в списке, и переход
      // ведёт к ней якорем — браузер прокрутит к нужной строке сам.
      path: `/app/instances#${якорьНомера(instance.id)}`,
    })),
  ]

  // Пустой запрос показывает разделы, а не пустоту: палитра, открытая
  // впервые, обязана объяснить собой, что она умеет.
  // Сравнение не различает «е» и «ё» (FR-ADM-038): какую из них набрали,
  // дело случая, а не смысла. Правило общее с палитрой нод — search.ts.
  const найденное =
    query.trim() === ''
      ? цели.filter((цель) => цель.kind === 'раздел')
      : цели.filter((цель) => подходитЗапросу(цель.title, query))

  const выбранный = найденное[Math.min(active, найденное.length - 1)]

  function перейти(цель: Цель | undefined) {
    if (цель === undefined) return
    onClose()
    navigate(цель.path)
  }

  function приНажатииКлавиши(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      перейти(выбранный)
      return
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return

    event.preventDefault()
    if (найденное.length === 0) return
    // По кругу: список короткий, и упереться в его край, промахнувшись на
    // одно нажатие, раздражает сильнее, чем проехать мимо.
    const шаг = event.key === 'ArrowDown' ? 1 : найденное.length - 1
    setActive((current) => (Math.min(current, найденное.length - 1) + шаг) % найденное.length)
  }

  return (
    <div
      className="палитра-команд"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div className="палитра-команд__окно" role="dialog" aria-modal="true" aria-label="Быстрый переход">
        <div className="палитра-команд__поле">
          <IconSearch />
          <input
            ref={поле}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={выбранный === undefined ? undefined : `${optionId}-${выбранный.key}`}
            aria-label="Куда перейти"
            placeholder="Раздел, сценарий или номер"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
              // Новый запрос — новый список: держать прежнее место значит
              // подсветить не ту строку, что стоит первой.
              setActive(0)
            }}
            onKeyDown={приНажатииКлавиши}
          />
        </div>

        {найденное.length === 0 ? (
          // Пустой список молчит о том, что произошло: не нашлось или ещё
          // не загрузилось. Поэтому — словами.
          <p className="палитра-команд__пусто">
            Ничего не нашлось по запросу {вКавычках(query)}. Попробуйте часть названия.
          </p>
        ) : (
          <ul className="палитра-команд__список" id={listId} role="listbox" aria-label="Что нашлось">
            {найденное.map((цель, index) => (
              <li
                key={цель.key}
                id={`${optionId}-${цель.key}`}
                role="option"
                aria-selected={цель.key === выбранный?.key}
                className={`палитра-команд__строка${цель.key === выбранный?.key ? ' палитра-команд__строка--выбрана' : ''}`}
                // Выбранная строка подтягивается в кадр: стрелкой вниз
                // уезжают за нижний край раньше, чем список прокрутится
                // сам, — и человек жмёт Enter, не видя, что выбрал.
                ref={цель.key === выбранный?.key ? подтянутьВКадр : null}
                onMouseEnter={() => setActive(index)}
                onMouseDown={(event) => {
                  // mousedown, а не click: click приходит после того, как
                  // поле уже потеряло фокус, и порядок закрытия путается.
                  event.preventDefault()
                  перейти(цель)
                }}
              >
                <span className="палитра-команд__название">{цель.title}</span>
                <span className="палитра-команд__вид">{ПОДПИСЬ_ВИДА[цель.kind]}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
